import {
  type Asset,
  DEFAULT_IMAGE_MODEL,
  ENHANCE_MODELS,
  type EnhanceModel,
  enhanceModelFor,
  enhanceProblem,
  enhanceScaleChoices,
  enhanceSourceSize,
  IMAGE_DEFAULT_NEGATIVE_PROMPT,
  IMAGE_MODELS,
  IMAGE_SIZES,
  type ImageAspect,
  type ImageModel,
  imageModelTakesReferences,
  type Job,
  MAX_REFERENCE_AUDIOS,
  MAX_REFERENCE_FILES,
  MAX_REFERENCE_IMAGES,
  MAX_REFERENCE_VIDEOS,
  maxImageReferences,
  type SpeechLanguage,
  type SpeechMode,
  type SpeechSpeaker,
  type SpeedMode,
  type VideoAspect,
  type VideoQuality,
  type VideoResolution,
  VOICE_REF_MAX_SEC,
  VOICE_REF_MIN_SEC,
} from "@studio/shared";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ImageDraft {
  prompt: string;
  /** What to steer away from; only sent to a model that takes one. Starts as the realism default. */
  negativePrompt: string;
  model: ImageModel;
  aspect: ImageAspect;
  /** Exact output canvas. Both sides are multiples of 64 within the image model's pixel budget. */
  width: number;
  height: number;
  count: number;
  /** Original or marked-up pictures Qwen should change or use as visual direction. */
  references: Asset[];
  /** Carried from the job this draft was loaded from, so the same roll can be run again. */
  seed: number | null;
  /** Sent only while a GPU server offers a fast path for the model; kept as the person's choice meanwhile. */
  speed: SpeedMode;
  /**
   * Let the model's prompt upsampler expand the prompt first (imageModelUpsamples); sent only to such a model.
   * Drafts saved before it existed have none, which counts as on.
   */
  upsample?: boolean;
}

export interface VideoDraft {
  prompt: string;
  resolution: VideoResolution;
  aspect: VideoAspect;
  frames: number;
  quality: VideoQuality;
  firstFrame: Asset | null;
  lastFrame: Asset | null;
  references: Asset[];
  /** Carried from the job this draft was loaded from, so the same roll can be run again. */
  seed: number | null;
  /** Sent only while a GPU server offers a fast path for H3; kept as the person's choice meanwhile. */
  speed: SpeedMode;
}

export interface MusicDraft {
  style: string;
  lyrics: string;
  instrumental: boolean;
  durationSec: number;
  withLyrics: boolean;
  /** Carried from the job this draft was loaded from, so the same roll can be run again. */
  seed: number | null;
}

export interface SpeechDraft {
  /** What should be said. */
  text: string;
  mode: SpeechMode;
  /** speaker mode: which packaged voice. */
  speaker: SpeechSpeaker;
  /** clone mode: a saved voice, or a clip used once (whichever was picked last). */
  voiceId: string | null;
  voiceRef: Asset | null;
  /** clone mode: what that clip says. */
  referenceText: string;
  /** speaker mode: how to say it; design mode: who is speaking. */
  instruct: string;
  language: SpeechLanguage;
  /** Carried from the job this draft was loaded from, so the same roll can be run again. */
  seed: number | null;
}

/** Restoring and enlarging a file that already exists: no prompt, one source. */
export interface EnhanceDraft {
  /** The picture or video to work on. */
  source: Asset | null;
  /** The model that was picked; null is the usual one for the kind of the source (see enhanceDraftModel). */
  model: EnhanceModel | null;
  /** Output size relative to the source, where the model offers a choice. */
  scale: number;
  /** Carried from the job this draft was loaded from, so the same roll can be run again. */
  seed: number | null;
}

export type Slot = "first" | "last" | "reference";

export const DEFAULT_IMAGE: ImageDraft = {
  prompt: "",
  negativePrompt: IMAGE_DEFAULT_NEGATIVE_PROMPT,
  model: DEFAULT_IMAGE_MODEL,
  aspect: "1:1",
  width: 1024,
  height: 1024,
  count: 2,
  references: [],
  seed: null,
  speed: "fast",
};
export const DEFAULT_VIDEO: VideoDraft = {
  prompt: "",
  resolution: "720p",
  aspect: "16:9",
  frames: 73,
  quality: "standard",
  firstFrame: null,
  lastFrame: null,
  references: [],
  seed: null,
  speed: "fast",
};
export const DEFAULT_MUSIC: MusicDraft = {
  style: "",
  lyrics: "",
  instrumental: false,
  durationSec: 60,
  withLyrics: false,
  seed: null,
};
export const DEFAULT_SPEECH: SpeechDraft = {
  text: "",
  mode: "speaker",
  speaker: "vivian",
  voiceId: null,
  voiceRef: null,
  referenceText: "",
  instruct: "",
  language: "auto",
  seed: null,
};

export const DEFAULT_ENHANCE: EnhanceDraft = { source: null, model: null, scale: 2, seed: null };

/**
 * The model a draft runs with: the one that was picked while it works on files of this kind, otherwise the usual
 * one for the kind. Null when nothing works on it.
 */
export function enhanceDraftModel(kind: Asset["kind"], picked: EnhanceModel | null): EnhanceModel | null {
  return picked && ENHANCE_MODELS[picked]?.input === kind ? picked : enhanceModelFor(kind);
}

/**
 * The scale to start from for this file: the one asked for when the model offers it, the file fits at it and it
 * is not already past the largest size the model writes; otherwise the largest one below it that is worth
 * offering. Null when no scale fits (the file is too large, or of a kind nothing works on).
 */
export function enhanceScaleFor(
  asset: Asset,
  wanted: number,
  model: EnhanceModel | null = enhanceModelFor(asset.kind),
): number | null {
  const size = enhanceSourceSize(asset);
  if (!model || !size.width || !size.height) return null;
  const fits = (scale: number) => enhanceProblem({ model, scale, kind: asset.kind, ...size }) === null;
  const offered = enhanceScaleChoices(model, size.width, size.height)
    .filter((c) => !c.same && fits(c.scale))
    .map((c) => c.scale);
  if (offered.includes(wanted)) return wanted;
  return [...offered].reverse().find((s) => s <= wanted) ?? offered[0] ?? null;
}

interface ComposerState {
  image: ImageDraft;
  video: VideoDraft;
  music: MusicDraft;
  speech: SpeechDraft;
  enhance: EnhanceDraft;
  /** Job the next run continues (edit/remix lineage). */
  parentJobId: string | null;
  /**
   * Whether the run being edited keeps the seed it was made with. Kept beside the lineage rather than in the
   * drafts so turning it off is not destructive: the seed stays, it is simply not sent, and it can come back.
   */
  reuseSeed: boolean;
  setImage: (patch: Partial<ImageDraft>) => void;
  setVideo: (patch: Partial<VideoDraft>) => void;
  setMusic: (patch: Partial<MusicDraft>) => void;
  setSpeech: (patch: Partial<SpeechDraft>) => void;
  setEnhance: (patch: Partial<EnhanceDraft>) => void;
  /** Makes a picture or video the file to enhance; returns a message when it cannot be one. */
  setEnhanceSource: (asset: Asset) => string | null;
  /** Adds a picture to Qwen's edit references; returns a message when it cannot be added. */
  addImageReference: (asset: Asset) => string | null;
  removeImageReference: (assetId: string) => void;
  /** Replaces an original reference with the marked-up copy produced by the drawing editor. */
  replaceImageReference: (assetId: string, replacement: Asset) => void;
  /**
   * Adds a close-up cut from a reference right after it (picture edits or video references); returns a message
   * when it cannot be added.
   */
  addCloseUp: (kind: "image" | "video", sourceId: string, crop: Asset) => string | null;
  setParent: (jobId: string | null) => void;
  setReuseSeed: (reuseSeed: boolean) => void;
  /** Puts an asset into a video slot, keeping the H3 rules; returns a note when something had to move. */
  placeInVideo: (asset: Asset, slot: Slot) => string | null;
  removeFromVideo: (assetId: string) => void;
  clear: (kind: "image" | "video" | "music" | "speech" | "enhance") => void;
}

function referenceCounts(references: Asset[]) {
  return {
    image: references.filter((a) => a.kind === "image").length,
    video: references.filter((a) => a.kind === "video").length,
    audio: references.filter((a) => a.kind === "audio").length,
  };
}

/** Why a picture cannot be used as an image-edit reference, or null when it can. */
export function imageReferenceBlocker(references: Asset[], asset: Asset, model: ImageModel): string | null {
  if (asset.kind !== "image") return "An edit works from pictures, not clips or sounds.";
  if (references.some((a) => a.id === asset.id)) return "It is already a reference.";
  const max = maxImageReferences(model);
  if (references.length >= max)
    return `${IMAGE_MODELS[model].label} works from at most ${max} pictures in one edit.`;
  return null;
}

/** The model an edit runs on: the chosen one when it can edit, otherwise Qwen, which takes the most pictures. */
function editingModel(model: ImageModel): ImageModel {
  return imageModelTakesReferences(model) ? model : "qwen-image-2.1";
}

/** Why an asset cannot be added as a reference now, or null when it can. */
export function referenceBlocker(references: Asset[], asset: Asset): string | null {
  if (asset.kind === "document") return "A PDF cannot guide a video. Attach it in a chat to ask about it.";
  if (references.some((a) => a.id === asset.id)) return "It is already a reference.";
  if (references.length >= MAX_REFERENCE_FILES) return "Use at most 12 references in one video.";
  const counts = referenceCounts(references);
  if (asset.kind === "image" && counts.image >= MAX_REFERENCE_IMAGES)
    return "Use at most 9 reference images.";
  if (asset.kind === "video" && counts.video >= MAX_REFERENCE_VIDEOS) return "Use at most 3 reference clips.";
  if (asset.kind === "audio" && counts.audio >= MAX_REFERENCE_AUDIOS)
    return "Use at most 3 reference sounds.";
  if ((asset.kind === "video" || asset.kind === "audio") && asset.durationSec !== null) {
    if (asset.durationSec < 2) return "Reference clips and sounds need at least 2 seconds.";
    if (asset.durationSec > 15.5) return "Trim reference clips and sounds to 15 seconds or less.";
  }
  return null;
}

/**
 * Why a clip cannot carry a voice, or null when it can: cloning wants a few clean seconds of one person,
 * not a whole song. Unknown lengths pass — the server measures the file itself.
 */
export function voiceClipBlocker(durationSec: number | null): string | null {
  if (durationSec === null) return null;
  if (durationSec < VOICE_REF_MIN_SEC)
    return `A voice needs at least ${VOICE_REF_MIN_SEC} seconds of speech.`;
  if (durationSec > VOICE_REF_MAX_SEC)
    return `A voice is cloned from at most ${VOICE_REF_MAX_SEC} seconds. Trim the clip.`;
  return null;
}

function aspectOf(ratio: number): VideoDraft["aspect"] {
  if (ratio > 1.2) return "16:9";
  if (ratio < 1 / 1.2) return "9:16";
  return "1:1";
}

export const useComposer = create<ComposerState>()(
  persist(
    (set, get) => ({
      image: DEFAULT_IMAGE,
      video: DEFAULT_VIDEO,
      music: DEFAULT_MUSIC,
      speech: DEFAULT_SPEECH,
      enhance: DEFAULT_ENHANCE,
      parentJobId: null,
      reuseSeed: true,
      setImage: (patch) => set((s) => ({ image: { ...s.image, ...patch } })),
      setVideo: (patch) => set((s) => ({ video: { ...s.video, ...patch } })),
      setMusic: (patch) => set((s) => ({ music: { ...s.music, ...patch } })),
      setSpeech: (patch) => set((s) => ({ speech: { ...s.speech, ...patch } })),
      setEnhance: (patch) => set((s) => ({ enhance: { ...s.enhance, ...patch } })),
      setEnhanceSource: (asset) => {
        const enhance = get().enhance;
        const model = enhanceDraftModel(asset.kind, enhance.model);
        if (!model) return "Only pictures and videos can be enhanced.";
        // the scale that was chosen is kept for the models that offer one: a model with a single size (HDR)
        // runs at it without changing what the next file starts from
        const scale = enhanceScaleFor(asset, enhance.scale, model);
        if (scale === null) {
          return (
            enhanceProblem({
              model,
              scale: ENHANCE_MODELS[model].scales[0] ?? 1,
              kind: asset.kind,
              ...enhanceSourceSize(asset),
            }) ?? "This file cannot be enhanced."
          );
        }
        const offers = ENHANCE_MODELS[model].scales.length > 1;
        set({
          enhance: { source: asset, model: enhance.model, scale: offers ? scale : enhance.scale, seed: null },
        });
        return null;
      },
      addImageReference: (asset) => {
        const image = get().image;
        const model = editingModel(image.model);
        const blocker = imageReferenceBlocker(image.references, asset, model);
        if (blocker) return blocker;
        set({ image: { ...image, model, references: [...image.references, asset] } });
        return null;
      },
      removeImageReference: (assetId) =>
        set((s) => ({
          image: { ...s.image, references: s.image.references.filter((a) => a.id !== assetId) },
        })),
      replaceImageReference: (assetId, replacement) =>
        set((s) => ({
          image: {
            ...s.image,
            model: editingModel(s.image.model),
            references: s.image.references.map((a) => (a.id === assetId ? replacement : a)),
          },
        })),
      addCloseUp: (kind, sourceId, crop) => {
        const insert = (list: Asset[]) => {
          const at = list.findIndex((a) => a.id === sourceId);
          return at < 0 ? [...list, crop] : [...list.slice(0, at + 1), crop, ...list.slice(at + 1)];
        };
        if (kind === "image") {
          const image = get().image;
          const model = editingModel(image.model);
          const blocker = imageReferenceBlocker(image.references, crop, model);
          if (blocker) return blocker;
          set({ image: { ...image, model, references: insert(image.references) } });
          return null;
        }
        const video = get().video;
        const blocker = referenceBlocker(video.references, crop);
        if (blocker) return blocker;
        set({ video: { ...video, references: insert(video.references) } });
        return null;
      },
      setParent: (parentJobId) => set({ parentJobId }),
      setReuseSeed: (reuseSeed) => set({ reuseSeed }),
      placeInVideo: (asset, slot) => {
        const video = get().video;
        if (slot === "first" || slot === "last") {
          if (asset.kind !== "image") return "Start and end frames must be images.";
          const note = video.references.length
            ? "References were cleared: a video uses frames or references."
            : null;
          // The first picture decides the shape of the film, so a square photo is not squeezed into 16:9.
          const setsShape = slot === "first" || !video.firstFrame;
          const aspect =
            setsShape && asset.width && asset.height ? aspectOf(asset.width / asset.height) : video.aspect;
          set({
            video: {
              ...video,
              aspect,
              references: [],
              [slot === "first" ? "firstFrame" : "lastFrame"]: asset,
            },
          });
          return note;
        }
        const blocker = referenceBlocker(video.references, asset);
        if (blocker) return blocker;
        const note =
          video.firstFrame || video.lastFrame
            ? "Start and end frames were cleared: a video uses frames or references."
            : null;
        set({
          video: { ...video, firstFrame: null, lastFrame: null, references: [...video.references, asset] },
        });
        return note;
      },
      removeFromVideo: (assetId) =>
        set((s) => ({
          video: {
            ...s.video,
            firstFrame: s.video.firstFrame?.id === assetId ? null : s.video.firstFrame,
            lastFrame: s.video.lastFrame?.id === assetId ? null : s.video.lastFrame,
            references: s.video.references.filter((a) => a.id !== assetId),
          },
        })),
      // Starting fresh keeps the speed: it is the person's standing choice, not part of one draft.
      clear: (kind) =>
        set((s) =>
          kind === "image"
            ? { image: { ...DEFAULT_IMAGE, speed: s.image.speed } }
            : kind === "video"
              ? { video: { ...DEFAULT_VIDEO, speed: s.video.speed } }
              : kind === "music"
                ? { music: DEFAULT_MUSIC }
                : kind === "enhance"
                  ? { enhance: DEFAULT_ENHANCE }
                  : { speech: DEFAULT_SPEECH },
        ),
    }),
    {
      name: "studio-composer",
      version: 6,
      migrate: (persisted) => {
        const saved = persisted as Partial<ComposerState>;
        return {
          ...saved,
          image: { ...DEFAULT_IMAGE, ...saved.image },
          video: { ...DEFAULT_VIDEO, ...saved.video },
          enhance: { ...DEFAULT_ENHANCE, ...saved.enhance },
        } as ComposerState;
      },
    },
  ),
);

/** Loads a finished job's request into the composer so the person can change one thing and run it again. */
export async function loadJobIntoComposer(
  job: Job,
  fetchAsset: (id: string) => Promise<Asset>,
): Promise<void> {
  const composer = useComposer.getState();
  const request = job.request;
  const load = async (id: string | undefined) => (id ? fetchAsset(id).catch(() => null) : null);
  composer.setParent(job.id);
  composer.setReuseSeed(true);
  if (request.type === "image") {
    const references = await Promise.all(request.referenceImageAssetIds.map(load));
    composer.setImage({
      prompt: request.prompt,
      negativePrompt: request.negativePrompt ?? IMAGE_DEFAULT_NEGATIVE_PROMPT,
      model: request.model,
      aspect: request.aspect,
      width: request.width ?? IMAGE_SIZES[request.aspect].width,
      height: request.height ?? IMAGE_SIZES[request.aspect].height,
      count: request.count,
      references: references.filter((a): a is Asset => a !== null),
      seed: request.seed ?? null,
      // a run that asked for a speed brings it along; one that did not leaves the person's own choice alone
      ...(request.speed ? { speed: request.speed } : {}),
      ...(request.upsample !== undefined ? { upsample: request.upsample } : {}),
    });
    return;
  }
  if (request.type === "music") {
    composer.setMusic({
      style: request.style,
      lyrics: request.lyrics,
      instrumental: request.instrumental,
      durationSec: request.durationSec,
      withLyrics: Boolean(request.lyrics.trim()),
      seed: request.seed ?? null,
    });
    return;
  }
  if (request.type === "speech") {
    // A saved voice wins: the server resolved it to a clip when the job ran, but the person picked the name.
    composer.setSpeech({
      text: request.text,
      mode: request.mode,
      speaker: request.speaker,
      voiceId: request.voiceId ?? null,
      voiceRef: request.voiceId ? null : await load(request.voiceRefAssetId),
      referenceText: request.referenceText,
      instruct: request.instruct,
      language: request.language,
      seed: request.seed ?? null,
    });
    return;
  }
  if (request.type === "enhance") {
    composer.setEnhance({
      source: await load(request.sourceAssetId),
      model: request.model,
      // a job of a model with a single size says nothing about the scale the person likes
      ...(ENHANCE_MODELS[request.model].scales.length > 1 ? { scale: request.scale } : {}),
      seed: request.seed ?? null,
    });
    return;
  }
  const [firstFrame, lastFrame, ...references] = await Promise.all([
    load(request.firstFrameAssetId),
    load(request.lastFrameAssetId),
    ...[
      ...request.referenceImageAssetIds,
      ...request.referenceVideoAssetIds,
      ...request.referenceAudioAssetIds,
    ].map(load),
  ]);
  composer.setVideo({
    prompt: request.prompt,
    resolution: request.resolution,
    aspect: request.aspect,
    frames: request.frames,
    quality: request.quality,
    firstFrame: firstFrame ?? null,
    lastFrame: lastFrame ?? null,
    references: references.filter((a): a is Asset => a !== null),
    seed: request.seed ?? null,
    ...(request.speed ? { speed: request.speed } : {}),
  });
}
